// File: frontend/src/contexts/ThemeContext.jsx
// Theme context for managing dark/light mode

import { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { getUserSettings, updateUserSetting } from '../services/api';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('light');
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  // Load theme from user settings
  useEffect(() => {
    const loadTheme = async () => {
      try {
        if (user) {
          // Try to get theme from user settings
          const userSettings = await getUserSettings();
          const settings = userSettings.data.settings;
          
          // Find theme preference in settings
          let themePreference = 'light';
          if (settings && settings.appearance) {
            const themeSetting = settings.appearance.find(s => s.setting_key === 'theme_preference');
            if (themeSetting) {
              themePreference = themeSetting.setting_value;
            }
          }

          // Handle auto theme (system preference)
          if (themePreference === 'auto') {
            themePreference = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
          }

          setTheme(themePreference);
        } else {
          // No user, check localStorage or default to light
          const savedTheme = localStorage.getItem('crm-theme') || 'light';
          setTheme(savedTheme);
        }
      } catch (error) {
        console.error('Failed to load theme:', error);
        // Fallback to localStorage or light theme
        const savedTheme = localStorage.getItem('crm-theme') || 'light';
        setTheme(savedTheme);
      } finally {
        setIsLoading(false);
      }
    };

    loadTheme();
  }, [user]);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    
    if (theme === 'dark') {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
    }

    // Also save to localStorage for persistence
    localStorage.setItem('crm-theme', theme);
    
    console.log('🎨 Theme applied:', theme);
  }, [theme]);

  // Handle system theme changes when theme is 'auto'
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleSystemThemeChange = async () => {
      if (user) {
        // Check if user has 'auto' theme preference
        try {
          const userSettings = await getUserSettings();
          const settings = userSettings.data.settings;
          
          if (settings && settings.appearance) {
            const themeSetting = settings.appearance.find(s => s.setting_key === 'theme_preference');
            if (themeSetting && themeSetting.setting_value === 'auto') {
              const newTheme = mediaQuery.matches ? 'dark' : 'light';
              setTheme(newTheme);
            }
          }
        } catch (error) {
          console.error('Failed to check theme preference:', error);
        }
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
  }, [user]);

  const toggleTheme = async () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);

    // Save to user settings if logged in
    if (user) {
      try {
        await updateUserSetting('theme_preference', newTheme);
        console.log('✅ Theme preference saved to user settings');
      } catch (error) {
        console.error('❌ Failed to save theme preference:', error);
      }
    }
  };

  const setThemePreference = async (preference) => {
    let actualTheme = preference;
    
    // Handle auto theme
    if (preference === 'auto') {
      actualTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }

    setTheme(actualTheme);

    // Save to user settings if logged in
    if (user) {
      try {
        await updateUserSetting('theme_preference', preference);
        console.log('✅ Theme preference saved to user settings');
      } catch (error) {
        console.error('❌ Failed to save theme preference:', error);
      }
    }
  };

  const value = {
    theme,
    isLoading,
    toggleTheme,
    setThemePreference,
    isDark: theme === 'dark'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeProvider;